import { useGlobalization } from '@Translations';
import {
	TimeSeriesUploadWithFile,
	UPLOAD_FILE_STATUS_TEXT,
	UploadFileStatusType,
} from '@Types/sensor.types';
import { timezones } from '@Utils/timeZones';
import {
	FORM_MODES,
	Features,
	FileDropzone,
	InfoCard,
	InputAutocomplete,
	StylovyzeForm,
	TextOverflow,
	palette,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { Grid } from '@mui/material';
import { getValidDateFormat } from '@Utils/validDateFormat';
import { ChangeEvent, Dispatch, default as React, SetStateAction } from 'react';
import { DropEvent, FileError, FileRejection } from 'react-dropzone';
import { Option } from '../../components/GeneralSettings';
import {
	Container,
	StyledLi,
	StyledUl,
	ValidationInfoTitle,
} from './SensorDataUploader.styles';
import Papa, { ParseError } from 'papaparse';

type PPSTEP_CSV_RECORD = Record<string, string | number | undefined>;

interface SensorDataUploaderProps {
	uploadFiles: Array<TimeSeriesUploadWithFile>;
	uploadStatus: UploadFileStatusType;
	setUploadStatus: Dispatch<SetStateAction<UploadFileStatusType>>;
	setUploadFiles: Dispatch<SetStateAction<Array<TimeSeriesUploadWithFile>>>;
	setFilesTimeZone: Dispatch<SetStateAction<string>>;
	setSubmitButtonDisabled: React.Dispatch<SetStateAction<boolean>>;
	setSpinning: React.Dispatch<SetStateAction<boolean>>;
	setSpinnerText: Dispatch<SetStateAction<string>>;
}

interface FileWarning {
	message: string;
	code: string;
	type: 'warning';
}
interface FileWithCsv extends File {
	csv: string[][];
	error: string;
	warnings?: FileWarning[];
}

interface ValidationWarning {
	file: File;
	warnings: FileWarning[];
}

const CSV_FILE_SIZE_LIMIT_BYTES = 25_000_000; // Do not change this value without addressing I360I-188 !!!

const timeZoneOptions = timezones.map(key => ({
	key,
	value: key === 'UTC' ? 'UTC (default)' : key.replace('_', ' '),
}));

export default function SensorDataUploader({
	uploadFiles,
	setUploadFiles,
	setFilesTimeZone,
	setSubmitButtonDisabled,
	setSpinning,
	setSpinnerText,
	uploadStatus,
	setUploadStatus,
}: SensorDataUploaderProps) {
	const { t } = useGlobalization();

	const [validationErrors, setValidationErrors] = React.useState<
		FileRejection[]
	>([]);

	const [validationWarnings, setValidationWarnings] = React.useState<
		ValidationWarning[]
	>([]);

	const getFilesFromEvent = async (
		event: DropEvent,
	): Promise<Array<File | DataTransferItem>> => {
		fileErrors = {};
		fileWarnings = {};
		sampleCount = {};
		const files: File[] = Array.from(
			((event as React.ChangeEvent<HTMLInputElement>)?.target?.files ||
				(event as DragEvent)?.dataTransfer?.files) as ArrayLike<File>,
		);
		//setUploadFiles(files as unknown as TimeSeriesUploadWithFile[]);
		try {
			if (files?.length) {
				setSpinnerText(t('Scanning'));
				setUploadStatus('scanning');
				setSpinning(true);
				await parseFiles(files);
			}
		} catch (e) {
			setUploadStatus('error');
		} finally {
			setSpinning(false);
		}
		return files;
	};

	const parseFiles = async (files: File[]) => {
		return Promise.all(
			files.map(async (file: File): Promise<void> => {
				let recCnt = 0;
				return new Promise((resolve, reject) => {
					if (file.size >= CSV_FILE_SIZE_LIMIT_BYTES) {
						fileErrors[file.name] = [fileIsTooLargeError];
						console.error(
							`csv file too large (${file.size}).  Must be < ${
								CSV_FILE_SIZE_LIMIT_BYTES / 1_000_000
							} MB`,
						);
						return reject('csv file too large');
					}
					Papa.parse(file, {
						delimiter: '', // auto-detect
						newline: undefined, // auto-detect
						quoteChar: '"',
						escapeChar: '"',
						header: true,
						transformHeader: undefined,
						dynamicTyping: true,
						preview: 0,
						encoding: 'UTF-8',
						worker: false,
						comments: false,
						skipEmptyLines: true,
						chunk: undefined,
						chunkSize: undefined,
						fastMode: undefined,
						beforeFirstChunk: undefined,
						transform: undefined,
						delimitersToGuess: [
							',',
							'\t',
							'|',
							';',
							Papa.RECORD_SEP,
							Papa.UNIT_SEP,
						],
						complete: (results, file) => {
							if (!recCnt) {
								if (!fileErrors[file.name])
									fileErrors[file.name] = [];
								fileErrors[file.name].push(fileIsEmptyError);
								reject('empty file');
							} else {
								resolve();
							}
						},
						step: (
							result: Papa.ParseStepResult<PPSTEP_CSV_RECORD>,
							parser,
						) => {
							recCnt += 1;
							if (!validRecord(result, recCnt, file.name)) {
								console.error('Aborting CSV parser');
								parser.abort();
								reject('invalid file');
							}
						},
						error: (e, file) => {
							console.error('Error', e, 'records.');
							console.error('Error', file, 'records.');
							const parsingError = e?.message
								? e.message
								: 'Error while parsing csv';
							Object.defineProperty(file, 'csv', { value: [] });
							Object.defineProperty(file, 'error', {
								value: parsingError,
							});
							setSpinning(false);
							setUploadStatus('error');
							reject('unexpected parsing error');
						},
					});
				});
			}),
		);
	};

	const CSV_SENSOR_COUNT_LIMIT = 100; // Do not change this value without addressing I360I-188 !!!
	const csvParsingError = (error: string): FileError => ({
		message: error,
		code: 'csv-parsing-error',
	});
	const fileIsEmptyError: FileError = {
		message: t('File is empty'),
		code: 'file-is-empty',
	};
	const fileIsTooLargeError: FileError = {
		message: t(
			`File is too large.  Must be < ${
				CSV_FILE_SIZE_LIMIT_BYTES / 1_000_000
			} MB`,
		),
		code: 'file-is-too-large',
	};
	const tooManySensors: FileError = {
		message: t(`File contains > ${CSV_SENSOR_COUNT_LIMIT} sensors`),
		code: 'too-many-sensors',
	};
	const sensorNameNotFoundError: FileError = {
		message: t('Sensor name not found in column header'),
		code: 'sensor-name-not-found',
	};
	const invalidDateError = (row: number, date: string): FileError => ({
		message: t(`Invalid date "${date}" on line ${row + 1}`),
		code: 'invalid-date',
	});
	const sensorValueNotFoundWarning = (row: number): FileWarning => ({
		message: t(`Sensor value not found on line ${row + 1}`),
		code: 'sensor-value-not-found',
		type: 'warning',
	});
	const invalidSensorName = (sensorName: string): FileWarning => ({
		message: t(
			`Sensor name contains nonstandard characters (${sensorName})`,
		),
		code: 'abnormal-sensor-name',
		type: 'warning',
	});

	const MAX_ERROR_COUNT = 5; // show the first 5 errors for a file
	const MAX_WARNING_COUNT = 5; // show the first 5 warnings for a file
	let fileErrors: Record<string, FileError[]> = {};
	let fileWarnings: Record<string, FileWarning[]> = {};
	let sampleCount: Record<string, number> = {};

	const validRecord = (
		item: Papa.ParseStepResult<PPSTEP_CSV_RECORD>,
		recCnt: number,
		fileName: string,
	): boolean => {
		// empty record
		if (!item) {
			// should be skipped automatically by parser
			console.warn('validRecord - empty.  how did I get here?!!!');
			return true;
		}

		let fe = fileErrors[fileName];
		let fw = fileWarnings[fileName];
		if (!fe) {
			fileErrors[fileName] = [];
			fe = fileErrors[fileName];
		}
		if (!fw) {
			fileWarnings[fileName] = [];
			fw = fileWarnings[fileName];
		}

		// validate sensor limit
		if (
			item.meta?.fields?.length &&
			item.meta?.fields?.length > CSV_SENSOR_COUNT_LIMIT + 1
		) {
			if (fe.length < MAX_ERROR_COUNT) {
				fe.push(tooManySensors);
				return false;
			}
		}

		// report errors detected by parser
		if (item.errors?.length) {
			const err: ParseError = item?.errors[0] || {};
			if (fe.length < MAX_WARNING_COUNT) {
				fe.push(csvParsingError(`[${err.row}] ${err.message}`));
			}
		}

		// validate date
		const dtField: string = item.meta?.fields?.[0] || '';
		const dateValue: string = (item.data?.[dtField] as string) || '';
		const dateFormat = getValidDateFormat(dateValue);
		if (dateFormat === undefined) {
			if (fe.length < MAX_ERROR_COUNT) {
				fe.push(invalidDateError(recCnt, dateValue));
			}
		}

		// verify each sensor receives at least 1 data point
		for (const [key, value] of Object.entries(item.data)) {
			if (key == dtField) continue;
			if (typeof value === 'number') {
				const cnt = sampleCount[key];
				sampleCount[key] = cnt ? cnt + 1 : 1; // needed anymore
			} else {
				if (fw.length < MAX_ERROR_COUNT) {
					fw.push(sensorValueNotFoundWarning(recCnt));
				}
			}
		}

		if (recCnt == 1) {
			// verify sensor names valid
			for (const key in item.data) {
				if (key == dtField) continue;
				if (key.match(/[^a-zA-Z0-9_.\- :]/)) {
					if (fe.length < MAX_ERROR_COUNT) {
						fe.push(invalidSensorName(key));
					}
				}

				// check for blank/white space (only) sensorID
				if (!key.match(/\S/)) {
					if (fe.length < MAX_ERROR_COUNT) {
						fe.push(sensorNameNotFoundError);
					}
				}
			}
		}

		// stop parsing if too many errrors
		return fe.length < MAX_ERROR_COUNT;
	};

	const validator = (file: File): FileError | FileError[] | null => {
		const errCnt = Object.keys(fileErrors).reduce((prev, key) => {
			return prev + fileErrors[key].length;
		}, 0);
		//const warnCnt = Object.keys(fileWarnings).reduce( (prev, key) => { return prev + fileWarnings[key].length }, 0);

		const warnings: FileError[] = Object.keys(fileWarnings).reduce(
			(prev, key) => {
				return [...prev, ...fileWarnings[key]];
			},
			[] as FileError[],
		);
		Object.defineProperty(file, 'warnings', {
			value: warnings,
		});

		if (errCnt === 0) {
			return null;
		}
		const errors: FileError[] = Object.keys(fileErrors).reduce(
			(prev, key) => {
				return [...prev, ...fileErrors[key]];
			},
			[] as FileError[],
		);
		if (errCnt === 1) {
			return errors[0];
		}
		return errors;
	};

	const onDropAccepted = async function (files: File[]) {
		setUploadFiles([
			...uploadFiles,
			...(files as unknown as TimeSeriesUploadWithFile[]),
		]);
		setUploadStatus('ready');
	};

	const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
		setValidationWarnings(
			acceptedFiles
				.filter(
					file => ((file as FileWithCsv)?.warnings ?? []).length > 0,
				)
				.map(file => ({
					file: file,
					warnings: (file as FileWithCsv).warnings ?? [],
				})),
		);
		setValidationErrors(fileRejections);
		if (uploadFiles?.length) {
			setSubmitButtonDisabled(false);
			setUploadStatus('ready');
		} else {
			setSubmitButtonDisabled(true);
			setUploadStatus('error');
		}
	};

	const handleTimeZoneChange = (
		_e: ChangeEvent<HTMLInputElement>,
		option?: Option,
	) => {
		if (option) {
			setFilesTimeZone(option.key);
		}
	};

	const isTimeZoneEnabled = useIsFeatureEnabled(
		'info360-upload-sensor-data-timezone-picker' as keyof Features,
	);

	const statusText = UPLOAD_FILE_STATUS_TEXT(t)[uploadStatus];
	const title = `${t('File Attachments')}${statusText && ' - '}${statusText}`;
	const getValidationResultString = (
		message: string,
		idx: number,
		lastElementIdx: number,
	): string => {
		let detail = idx === 0 ? ' - ' : '; ';
		detail += message;
		if (idx === lastElementIdx) detail += '.';
		return detail;
	};
	const fileValidationErrorInfo = validationErrors.map(validationErr => (
		<StyledLi
			color={palette.error.dark}
			key={validationErr.file.webkitRelativePath}>
			{validationErr.file.name}
			{validationErr.errors.map((err, idx) =>
				getValidationResultString(
					err.message,
					idx,
					validationErr.errors.length - 1,
				),
			)}
		</StyledLi>
	));
	const fileValidationWarningInfo = validationWarnings.map(
		validationWarning => (
			<StyledLi
				color={palette.warning.dark}
				key={validationWarning.file.webkitRelativePath}>
				{validationWarning.file.name}
				{validationWarning.warnings.map((warning, idx) =>
					getValidationResultString(
						warning.message,
						idx,
						validationWarning.warnings.length - 1,
					),
				)}
			</StyledLi>
		),
	);
	return (
		<Container id="create-bulk-sensor-uploader">
			<Container>
				<InfoCard
					dataCy="dropdownZone"
					fullHeight
					title={
						<Grid container>
							<Grid
								item
								xs={8}
								display="flex"
								alignItems="center">
								<TextOverflow>{title}</TextOverflow>
							</Grid>
							{isTimeZoneEnabled ? (
								<Grid item xs={4}>
									<StylovyzeForm
										mode={FORM_MODES.EDIT}
										initialValues={{ timezone: 'UTC' }}
										onSubmit={() => void 0}>
										{() => (
											<InputAutocomplete
												fieldAttrs={{
													name: 'timezone',
												}}
												options={timeZoneOptions}
												label={t('Time Zone')}
												dataCy="timeZone"
												autocompleteProps={{
													disableClearable: true,
												}}
												onChange={handleTimeZoneChange}
											/>
										)}
									</StylovyzeForm>
								</Grid>
							) : null}
						</Grid>
					}>
					<FileDropzone
						supportedFileText={t('Supported file type: csv')}
						dropzoneOptions={{
							accept: '.csv, text/csv',
							onDrop,
							onDropAccepted,
							getFilesFromEvent,
							validator,
						}}
						token={''}
					/>
				</InfoCard>
				<Grid container>
					{validationErrors.length > 0 && (
						<Grid item xs={12}>
							<Grid item xs={12}>
								<ValidationInfoTitle color={palette.error.dark}>
									{t('Validation Errors')}
								</ValidationInfoTitle>
							</Grid>
							<Grid item xs={12}>
								<StyledUl>{fileValidationErrorInfo}</StyledUl>
							</Grid>
						</Grid>
					)}
					{validationWarnings.length > 0 && (
						<Grid item xs={12}>
							<Grid item xs={12}>
								<ValidationInfoTitle
									color={palette.warning.dark}>
									{t('Validation Warnings')}
								</ValidationInfoTitle>
							</Grid>
							<Grid item xs={12}>
								<StyledUl>{fileValidationWarningInfo}</StyledUl>
							</Grid>
						</Grid>
					)}
				</Grid>
			</Container>
		</Container>
	);
}
